import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronDown, ChevronRight, ExternalLink, Loader2, Rocket, Wand2, Sparkles, Upload } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { resolveMediaUrl } from '../lib/mediaUrl';
import { useWhitelabelBrand } from '../context/WhitelabelBrandContext';

const API_BASE = (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== '')
  ? process.env.REACT_APP_BACKEND_URL
  : '';

const PLAN_KEYS = ['starter', 'growth', 'pro'];

const TEMPLATE_STYLES = {
  classic: 'from-violet-600/40 to-indigo-900/40 border-violet-500/40',
  bold: 'from-orange-600/50 to-amber-900/40 border-orange-500/40',
  minimal: 'from-zinc-200/10 to-zinc-500/20 border-zinc-400/30',
  agency: 'from-emerald-600/40 to-teal-900/40 border-emerald-500/40',
};

function formatApiDetail(data, fallback) {
  if (!data) return fallback;
  if (typeof data.detail === 'string') return data.detail;
  if (data.detail?.message) return data.detail.message;
  if (Array.isArray(data.detail)) return data.detail.map((d) => d.msg || JSON.stringify(d)).join('; ');
  return fallback;
}

function ensureThreePlans(cards, defaultsByKey) {
  const byKey = {};
  (cards || []).forEach((c) => {
    const k = (c.key || '').toLowerCase();
    if (PLAN_KEYS.includes(k)) byKey[k] = c;
  });
  return PLAN_KEYS.map((key, idx) => {
    const src = byKey[key] || (cards || [])[idx] || defaultsByKey?.[key] || {};
    return {
      key,
      name: src.name || key.charAt(0).toUpperCase() + key.slice(1),
      price: src.price || (key === 'starter' ? 'Free' : key === 'growth' ? '$39/mo' : '$99/mo'),
      cta: src.cta || 'Get started',
      description: src.description || '',
      highlight: !!src.highlight || key === 'growth',
      module_ids: Array.isArray(src.module_ids) ? [...src.module_ids] : (defaultsByKey?.[key]?.module_ids || []),
      feature_notes: Array.isArray(src.feature_notes) ? [...src.feature_notes] : [],
      features: Array.isArray(src.features) ? [...src.features] : [],
    };
  });
}

function ModuleTree({ groups, selectedIds, onToggle }) {
  const [openGroups, setOpenGroups] = useState(() => new Set(Object.keys(groups || {})));
  const selected = useMemo(() => new Set(selectedIds || []), [selectedIds]);

  return (
    <div className="max-h-64 overflow-y-auto rounded-xl border border-white/10 bg-black/30 p-2 space-y-1">
      {Object.entries(groups || {}).map(([group, modules]) => {
        const open = openGroups.has(group);
        const groupIds = modules.map((m) => m.id);
        const allOn = groupIds.every((id) => selected.has(id));
        const someOn = groupIds.some((id) => selected.has(id));
        return (
          <div key={group}>
            <button
              type="button"
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-xs font-semibold text-zinc-200 hover:bg-white/[0.04]"
              onClick={() => {
                setOpenGroups((prev) => {
                  const next = new Set(prev);
                  if (next.has(group)) next.delete(group);
                  else next.add(group);
                  return next;
                });
              }}
            >
              {open ? <ChevronDown className="w-3.5 h-3.5 text-zinc-500" /> : <ChevronRight className="w-3.5 h-3.5 text-zinc-500" />}
              <span className="flex-1">{group}</span>
              <label
                className="flex items-center gap-1 text-[10px] font-normal text-zinc-500"
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  checked={allOn}
                  ref={(el) => { if (el) el.indeterminate = !allOn && someOn; }}
                  onChange={() => {
                    if (allOn) groupIds.forEach((id) => onToggle(id, false));
                    else groupIds.forEach((id) => onToggle(id, true));
                  }}
                />
                all
              </label>
            </button>
            {open && (
              <div className="pl-6 pb-1 space-y-0.5">
                {modules.map((m) => (
                  <label
                    key={m.id}
                    className="flex items-start gap-2 px-2 py-1 rounded-md text-[11px] text-zinc-300 hover:bg-white/[0.03] cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={selected.has(m.id)}
                      onChange={(e) => onToggle(m.id, e.target.checked)}
                    />
                    <span>
                      <span className="text-white font-medium">{m.name}</span>
                      {m.desc ? <span className="block text-zinc-500 leading-snug">{m.desc}</span> : null}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function WhiteLabelSettingsPanel({ user }) {
  const navigate = useNavigate();
  const { applyBrand } = useWhitelabelBrand();
  const [whitelabel, setWhitelabel] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [clientsData, setClientsData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activePlanKey, setActivePlanKey] = useState('growth');
  const [domainSetup, setDomainSetup] = useState(null);
  const [domainBusy, setDomainBusy] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoProvider, setLogoProvider] = useState('gemini');
  const [logoStyle, setLogoStyle] = useState('');
  const fileInputRef = useRef(null);

  const handleGenerateLogo = async () => {
    setLogoBusy(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/whitelabel/logo/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          brand_name: whitelabel?.brand_name,
          style: logoStyle,
          provider: logoProvider,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiDetail(data, 'Logo generation failed'));
      const nextLogo = data.logo_url || data.logo_data_url;
      setWhitelabel((prev) => ({
        ...prev,
        logo_url: nextLogo,
        logo_data_url: data.logo_data_url || null,
        logo_missing: false,
      }));
      applyBrand({ logo_url: nextLogo, brand_name: whitelabel?.brand_name });
      if (data.warning) toast.warning(data.warning);
      else toast.success('AI logo generated and saved!');
    } catch (e) {
      toast.error(e.message || 'Logo generation failed');
    } finally {
      setLogoBusy(false);
    }
  };

  const handleUploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoBusy(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_BASE}/api/whitelabel/logo/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiDetail(data, 'Upload failed'));
      const nextLogo = data.logo_url || data.logo_data_url;
      setWhitelabel((prev) => ({
        ...prev,
        logo_url: nextLogo,
        logo_data_url: data.logo_data_url || null,
        logo_missing: false,
      }));
      applyBrand({ logo_url: nextLogo, brand_name: whitelabel?.brand_name });
      toast.success('Logo uploaded and saved!');
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setLogoBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [wlRes, catRes, clientsRes] = await Promise.all([
        fetch(`${API_BASE}/api/whitelabel/me`, { headers }),
        fetch(`${API_BASE}/api/whitelabel/reseller-catalog`, { headers }),
        fetch(`${API_BASE}/api/whitelabel/clients`, { headers }),
      ]);
      if (!wlRes.ok) {
        const data = await wlRes.json().catch(() => ({}));
        throw new Error(formatApiDetail(data, `Failed to load (${wlRes.status})`));
      }
      const wl = await wlRes.json();
      const cat = catRes.ok ? await catRes.json() : null;
      setCatalog(cat);
      if (clientsRes.ok) {
        setClientsData(await clientsRes.json());
      } else {
        setClientsData(null);
      }
      const defaultsByKey = {};
      PLAN_KEYS.forEach((k) => {
        defaultsByKey[k] = {
          module_ids: cat?.defaults?.[k]?.module_ids || [],
        };
      });
      const next = {
        ...wl,
        logo_url: wl.logo_url || null,
        logo_missing: Boolean(wl.logo_missing),
        landing_template: wl.landing_template || 'classic',
        pricing_cards: ensureThreePlans(wl.pricing_cards, defaultsByKey),
      };
      setWhitelabel(next);
      applyBrand({
        brand_name: next.brand_name,
        logo_url: next.logo_url,
        logo_missing: next.logo_missing,
      });
      try {
        const setupRes = await fetch(`${API_BASE}/api/whitelabel/domain-setup`, { headers });
        if (setupRes.ok) setDomainSetup(await setupRes.json());
      } catch {
        /* optional */
      }
    } catch (e) {
      setError(e.message || 'Could not load white-label settings');
    } finally {
      setLoading(false);
    }
  }, [applyBrand]);

  const verifyDomain = async () => {
    setDomainBusy(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/whitelabel/domain-verify`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiDetail(data, 'DNS check failed'));
      setDomainSetup((prev) => ({ ...(prev || {}), dns: data }));
      if (data.ok) toast.success('DNS looks good — domain points at the frontend');
      else toast.message(data.detail || 'DNS not ready yet — wait for propagation');
    } catch (err) {
      toast.error(err.message || 'DNS check failed');
    } finally {
      setDomainBusy(false);
    }
  };

  useEffect(() => { load(); }, [load]);

  const updatePlan = (key, patch) => {
    setWhitelabel((prev) => {
      if (!prev) return prev;
      const cards = ensureThreePlans(prev.pricing_cards);
      const idx = cards.findIndex((c) => c.key === key);
      if (idx < 0) return prev;
      cards[idx] = { ...cards[idx], ...patch };
      return { ...prev, pricing_cards: cards };
    });
  };

  const toggleModule = (planKey, moduleId, on) => {
    setWhitelabel((prev) => {
      if (!prev) return prev;
      const cards = ensureThreePlans(prev.pricing_cards);
      const idx = cards.findIndex((c) => c.key === planKey);
      if (idx < 0) return prev;
      const set = new Set(cards[idx].module_ids || []);
      if (on) set.add(moduleId);
      else set.delete(moduleId);
      cards[idx] = { ...cards[idx], module_ids: Array.from(set) };
      return { ...prev, pricing_cards: cards };
    });
  };

  const handleSave = async () => {
    if (!whitelabel) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        slug: whitelabel.slug,
        brand_name: whitelabel.brand_name,
        logo_url: whitelabel.logo_url,
        logo_data_url: whitelabel.logo_data_url || (
          String(whitelabel.logo_url || '').startsWith('data:') ? whitelabel.logo_url : undefined
        ),
        primary_color: whitelabel.primary_color,
        custom_domain: whitelabel.custom_domain,
        custom_subdomain: whitelabel.custom_subdomain,
        landing_template: whitelabel.landing_template || 'classic',
        pricing_cards: ensureThreePlans(whitelabel.pricing_cards).map((p) => ({
          key: p.key,
          name: p.name,
          price: p.price,
          cta: p.cta,
          description: p.description,
          highlight: p.highlight,
          module_ids: p.module_ids,
          feature_notes: (p.feature_notes_text != null
            ? String(p.feature_notes_text).split('\n')
            : (p.feature_notes || [])
          ).map((s) => String(s).trim()).filter(Boolean),
        })),
      };
      const res = await fetch(`${API_BASE}/api/whitelabel/me`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiDetail(data, 'Failed to save'));
      const next = {
        ...data,
        logo_url: resolveMediaUrl(data.logo_url),
        pricing_cards: ensureThreePlans(data.pricing_cards),
      };
      setWhitelabel(next);
      applyBrand({ brand_name: next.brand_name, logo_url: next.logo_url });
      toast.success('White-label settings saved');
    } catch (e) {
      toast.error(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="glass p-8 rounded-2xl flex items-center justify-center gap-2 text-zinc-400 py-16">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading white-label settings…
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass p-8 rounded-2xl text-center py-12">
        <p className="text-red-400 mb-4">{error}</p>
        <Button variant="outline" onClick={load}>Retry</Button>
      </div>
    );
  }

  if (!whitelabel) return null;

  const plans = ensureThreePlans(whitelabel.pricing_cards);
  const activePlan = plans.find((p) => p.key === activePlanKey) || plans[0];
  const templates = catalog?.landing_templates || [
    { id: 'classic', name: 'Classic', desc: 'Current landing' },
    { id: 'bold', name: 'Bold', desc: 'Dark hero' },
    { id: 'minimal', name: 'Minimal', desc: 'Light editorial' },
    { id: 'agency', name: 'Agency', desc: 'Plan-first' },
  ];
  const groups = catalog?.modules_by_group || {};

  return (
    <div className="glass p-6 md:p-8 rounded-2xl space-y-8" data-testid="whitelabel-settings-panel">
      <div>
        <h2 className="text-2xl font-heading font-bold mb-1">White-label</h2>
        <p className="text-sm text-zinc-400">
          Brand your portal, pick a landing template, and assign modules to three client plans.
        </p>
      </div>

      {/* Client directory */}
      <section className="space-y-3" data-testid="whitelabel-clients-section">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-zinc-500">Your clients</h3>
            <p className="text-xs text-zinc-500 mt-1">
              Users who registered under your white-label landing
            </p>
          </div>
          {clientsData?.summary ? (
            <p className="text-xs text-zinc-400">
              {clientsData.summary.total_clients} total · {clientsData.summary.paid_clients} paid · {clientsData.summary.free_clients} free
            </p>
          ) : null}
        </div>
        <div className="rounded-2xl border border-white/10 bg-black/20 overflow-hidden">
          {!clientsData?.clients?.length ? (
            <p className="text-sm text-zinc-500 py-8 text-center px-4">
              No clients yet. Share your landing link so people can register under your brand.
            </p>
          ) : (
            <div className="overflow-x-auto max-h-80 overflow-y-auto">
              <table className="w-full text-left text-sm">
                <thead className="sticky top-0 bg-zinc-950/95 text-[10px] uppercase tracking-wider text-zinc-500">
                  <tr>
                    <th className="px-4 py-2 font-medium">Name</th>
                    <th className="px-4 py-2 font-medium">Email</th>
                    <th className="px-4 py-2 font-medium">Country</th>
                    <th className="px-4 py-2 font-medium">Plan</th>
                    <th className="px-4 py-2 font-medium">Joined</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {clientsData.clients.map((row) => (
                    <tr key={row.id || row.email}>
                      <td className="px-4 py-2.5 text-zinc-200">{row.name || '—'}</td>
                      <td className="px-4 py-2.5 text-zinc-400">{row.email}</td>
                      <td className="px-4 py-2.5 text-zinc-300">{row.country || '—'}</td>
                      <td className="px-4 py-2.5 capitalize text-orange-200">
                        {row.whitelabel_plan || row.plan || 'basic'}
                      </td>
                      <td className="px-4 py-2.5 text-[11px] text-zinc-500">
                        {row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* Brand */}
      <section className="space-y-4">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-zinc-500">Brand</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-white mb-2 block">Brand / Company Name</Label>
            <Input
              value={whitelabel.brand_name || ''}
              onChange={(e) => setWhitelabel({ ...whitelabel, brand_name: e.target.value })}
              className="bg-zinc-900/50 border-zinc-800 rounded-xl h-11 text-white"
            />
          </div>
          <div>
            <Label className="text-white mb-2 block">Primary Color</Label>
            <Input
              type="color"
              value={whitelabel.primary_color || '#f97316'}
              onChange={(e) => setWhitelabel({ ...whitelabel, primary_color: e.target.value })}
              className="bg-zinc-900/50 border-zinc-800 rounded-xl h-11"
            />
          </div>
        </div>
        <div>
          <Label className="text-white mb-2 block">Logo</Label>

          {/* Live lockup preview: logo icon left + brand name right */}
          <div
            className="flex items-center gap-3 rounded-xl border border-white/10 bg-black/20 px-4 py-3 mb-3"
            data-testid="logo-lockup-preview"
          >
            <div
              className="h-12 w-12 rounded-xl overflow-hidden flex items-center justify-center shrink-0 shadow-lg"
              style={{
                background: whitelabel.logo_url
                  ? '#0a0a0a'
                  : `linear-gradient(135deg, ${whitelabel.primary_color || '#f97316'}, #f59e0b)`,
              }}
            >
              {whitelabel.logo_url ? (
                <img src={resolveMediaUrl(whitelabel.logo_url)} alt="Brand logo" className="h-full w-full object-contain" data-testid="logo-preview-img" />
              ) : (
                <Rocket className="h-6 w-6 text-white -rotate-45" />
              )}
            </div>
            <div className="min-w-0">
              <p className="font-bold text-white truncate text-lg leading-tight" data-testid="logo-preview-brandname">
                {whitelabel.brand_name || 'Your Brand'}
              </p>
              <p className="text-[11px] text-zinc-500">This lockup shows on your landing page & dashboard.</p>
              {whitelabel.logo_missing && (
                <p className="text-[11px] text-amber-300 mt-1" data-testid="logo-missing-hint">
                  Saved logo file is missing on the server (common after redeploy). Re-upload or regenerate — it will be stored durably.
                </p>
              )}
            </div>
          </div>

          {/* Generate with AI */}
          <div className="rounded-xl border border-violet-500/20 bg-violet-500/5 p-3 space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-violet-300">
              <Wand2 className="w-4 h-4" /> Generate with AI
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-zinc-400">Model</span>
              {[['gemini', 'Nano Banana'], ['openai', 'GPT Image']].map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setLogoProvider(id)}
                  data-testid={`logo-model-${id}`}
                  className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                    logoProvider === id ? 'bg-violet-500 text-white' : 'bg-white/5 text-zinc-400 hover:bg-white/10'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <Input
              value={logoStyle}
              onChange={(e) => setLogoStyle(e.target.value)}
              placeholder="Style hint (optional) — e.g. minimal, tech, playful, gradient"
              className="bg-zinc-900/50 border-zinc-800 rounded-xl h-10 text-white text-sm"
              data-testid="logo-style-input"
            />
            <Button
              type="button"
              onClick={handleGenerateLogo}
              disabled={logoBusy}
              className="bg-violet-500 hover:bg-violet-400 text-white rounded-full font-semibold"
              data-testid="logo-generate-btn"
            >
              {logoBusy ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Generating…</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-1.5" /> Generate logo</>
              )}
            </Button>
          </div>

          {/* Upload + clear */}
          <div className="flex items-center gap-2 mt-3">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleUploadLogo}
              data-testid="logo-file-input"
            />
            <Button
              type="button"
              variant="outline"
              className="border-zinc-700 rounded-xl h-11"
              onClick={() => fileInputRef.current?.click()}
              disabled={logoBusy}
              data-testid="logo-upload-btn"
            >
              <Upload className="w-4 h-4 mr-1.5" /> Upload logo
            </Button>
            {whitelabel.logo_url && (
              <Button
                type="button"
                variant="ghost"
                className="text-zinc-400 hover:text-white"
                onClick={() => setWhitelabel({ ...whitelabel, logo_url: '' })}
                data-testid="logo-clear-btn"
              >
                Clear
              </Button>
            )}
          </div>

          <Input
            value={whitelabel.logo_url || ''}
            onChange={(e) => setWhitelabel({ ...whitelabel, logo_url: e.target.value })}
            className="bg-zinc-900/50 border-zinc-800 rounded-xl h-11 text-white mt-3"
            placeholder="…or paste a logo URL (https://…)"
            data-testid="logo-url-input"
          />
        </div>
        <div>
          <Label className="text-white mb-2 block">Landing URL Slug</Label>
          <Input
            value={whitelabel.slug || ''}
            onChange={(e) => setWhitelabel({ ...whitelabel, slug: e.target.value })}
            className="bg-zinc-900/50 border-zinc-800 rounded-xl h-11 text-white font-mono"
          />
          <p className="text-xs text-zinc-500 mt-1">
            Live at <span className="font-mono text-zinc-300">/w/{whitelabel.slug || 'workspace'}</span>
          </p>
        </div>
      </section>

      <section className="space-y-3 rounded-2xl border border-orange-500/20 bg-orange-500/5 p-4">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-orange-200/80">
          Custom domain (agency portal)
        </h3>
        <p className="text-xs text-zinc-400 leading-relaxed">
          Point your own hostname at the Snagcart frontend so clients open your branded landing and login —
          not the platform URL. This is separate from customer website domains (Domains &amp; Hosting).
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-white mb-2 block">Custom domain</Label>
            <Input
              value={whitelabel.custom_domain || ''}
              onChange={(e) => setWhitelabel({ ...whitelabel, custom_domain: e.target.value })}
              className="bg-zinc-900/50 border-zinc-800 rounded-xl h-11 text-white"
              placeholder="app.yourdomain.com"
              data-testid="wl-custom-domain"
            />
          </div>
          <div>
            <Label className="text-white mb-2 block">Alternate host / subdomain</Label>
            <Input
              value={whitelabel.custom_subdomain || ''}
              onChange={(e) => setWhitelabel({ ...whitelabel, custom_subdomain: e.target.value })}
              className="bg-zinc-900/50 border-zinc-800 rounded-xl h-11 text-white"
              placeholder="agency.yourdomain.com"
              data-testid="wl-custom-subdomain"
            />
          </div>
        </div>
        <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-3 space-y-2 text-[11px] text-zinc-300">
          <p className="font-medium text-zinc-200">How to connect</p>
          <ol className="list-decimal list-inside space-y-1 text-zinc-400">
            <li>
              Create a <span className="text-zinc-200">CNAME</span> for your hostname →{' '}
              <span className="font-mono text-orange-200">
                {domainSetup?.cname_target || 'your frontend host'}
              </span>
            </li>
            <li>Add the same hostname as a custom domain on Vercel/Railway (frontend) so HTTPS works.</li>
            <li>Save white-label below, then open <span className="font-mono text-zinc-200">https://your-host/</span> — it maps to your landing.</li>
            <li>
              Login path on your domain: <span className="font-mono text-zinc-200">/auth</span>
              {' '}(same as{' '}
              <span className="font-mono text-zinc-200">/w/{whitelabel.slug || 'workspace'}/auth</span>).
            </li>
          </ol>
          <p className="text-zinc-500 pt-1">
            Fallback without custom DNS:{' '}
            <span className="font-mono text-zinc-400">/w/{whitelabel.slug || 'workspace'}</span>
          </p>
          {domainSetup?.dns && (
            <p className={domainSetup.dns.ok ? 'text-emerald-400' : 'text-amber-300'}>
              DNS status: {domainSetup.dns.detail || (domainSetup.dns.ok ? 'OK' : 'Pending')}
            </p>
          )}
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={domainBusy || !whitelabel.custom_domain}
            onClick={verifyDomain}
            className="border-orange-500/40 text-orange-200 mt-1"
            data-testid="wl-domain-verify"
          >
            {domainBusy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
            Check DNS
          </Button>
        </div>
      </section>

      {/* Landing templates */}
      <section className="space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-zinc-500">Landing page template</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {templates.map((t) => {
            const selected = (whitelabel.landing_template || 'classic') === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setWhitelabel({ ...whitelabel, landing_template: t.id })}
                className={`relative rounded-2xl border p-3 text-left transition-all bg-gradient-to-br ${TEMPLATE_STYLES[t.id] || TEMPLATE_STYLES.classic} ${
                  selected ? 'ring-2 ring-white/80 scale-[1.02]' : 'opacity-80 hover:opacity-100'
                }`}
              >
                {selected && (
                  <span className="absolute top-2 right-2 w-5 h-5 rounded-full bg-white text-black flex items-center justify-center">
                    <Check className="w-3 h-3" />
                  </span>
                )}
                <div className="h-16 rounded-lg bg-black/30 mb-2 border border-white/10" />
                <p className="text-sm font-semibold text-white">{t.name}</p>
                <p className="text-[11px] text-zinc-300 mt-0.5 leading-snug">{t.desc}</p>
              </button>
            );
          })}
        </div>
      </section>

      {/* Plans */}
      <section className="space-y-4">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wider text-zinc-500">Client plans (3)</h3>
          <p className="text-xs text-zinc-500 mt-1">
            Clients who sign up from your landing page get the modules you select for that plan.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {plans.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setActivePlanKey(p.key)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                activePlanKey === p.key
                  ? 'bg-orange-500 text-white'
                  : 'bg-white/5 text-zinc-400 hover:bg-white/10'
              }`}
            >
              {p.name || p.key}
              {p.highlight ? ' · Popular' : ''}
            </button>
          ))}
        </div>

        {activePlan && (
          <div className="rounded-2xl border border-white/10 bg-black/20 p-4 md:p-5 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-zinc-400 mb-1 block">Plan name</Label>
                <Input
                  value={activePlan.name}
                  onChange={(e) => updatePlan(activePlan.key, { name: e.target.value })}
                  className="bg-zinc-900/50 border-zinc-800 rounded-xl h-10 text-white"
                />
              </div>
              <div>
                <Label className="text-xs text-zinc-400 mb-1 block">Price</Label>
                <Input
                  value={activePlan.price}
                  onChange={(e) => updatePlan(activePlan.key, { price: e.target.value })}
                  className="bg-zinc-900/50 border-zinc-800 rounded-xl h-10 text-white"
                />
              </div>
              <div>
                <Label className="text-xs text-zinc-400 mb-1 block">CTA</Label>
                <Input
                  value={activePlan.cta}
                  onChange={(e) => updatePlan(activePlan.key, { cta: e.target.value })}
                  className="bg-zinc-900/50 border-zinc-800 rounded-xl h-10 text-white"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-400 mb-1 block">Description</Label>
              <Input
                value={activePlan.description}
                onChange={(e) => updatePlan(activePlan.key, { description: e.target.value })}
                className="bg-zinc-900/50 border-zinc-800 rounded-xl h-10 text-white"
              />
            </div>
            <label className="flex items-center gap-2 text-xs text-zinc-300">
              <input
                type="checkbox"
                checked={!!activePlan.highlight}
                onChange={(e) => {
                  // Only one highlighted plan
                  setWhitelabel((prev) => {
                    const cards = ensureThreePlans(prev.pricing_cards).map((c) => ({
                      ...c,
                      highlight: c.key === activePlan.key ? e.target.checked : false,
                    }));
                    return { ...prev, pricing_cards: cards };
                  });
                }}
              />
              Highlight as most popular
            </label>
            <div>
              <Label className="text-xs text-zinc-400 mb-1 block">
                Modules & submodules ({(activePlan.module_ids || []).length} selected)
              </Label>
              <ModuleTree
                groups={groups}
                selectedIds={activePlan.module_ids}
                onToggle={(id, on) => toggleModule(activePlan.key, id, on)}
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-400 mb-1 block">
                Custom feature bullets (optional — one per line; overrides module names on landing)
              </Label>
              <textarea
                className="w-full bg-zinc-900/50 border border-zinc-800 rounded-xl px-3 py-2 text-xs text-white min-h-[72px]"
                value={(activePlan.feature_notes || []).join('\n')}
                onChange={(e) => updatePlan(activePlan.key, {
                  feature_notes: e.target.value.split('\n'),
                  feature_notes_text: e.target.value,
                })}
                placeholder="Leave blank to auto-list selected module names"
              />
            </div>
          </div>
        )}
      </section>

      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-4 border-t border-white/10">
        <p className="text-xs text-zinc-500">
          Signed-in as {user?.email}. Clients register at your landing and receive plan modules immediately.
        </p>
        <div className="flex gap-2 justify-end">
          <Button
            type="button"
            variant="outline"
            className="border-zinc-700"
            onClick={() => navigate(`/w/${whitelabel.slug || 'workspace'}`)}
          >
            Preview <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-orange-500 hover:bg-orange-400 text-white rounded-full px-6 font-bold"
          >
            {saving ? 'Saving…' : 'Save white-label'}
          </Button>
        </div>
      </div>
    </div>
  );
}
